import React, { useState, useEffect } from 'react';
import { STEPS } from '../CheckoutModal';
import { apiFetch } from '../../../utils/api';

/**
 * Delivery method selection.
 *
 * Fetches Chowdeck (hyperlocal fast delivery) and Terminal Africa
 * (nationwide multi-carrier parcel) rates in PARALLEL as soon as
 * the customer's destination state is known. Renders whichever
 * options come back available.
 *
 * Terminal Africa uses the "A2 hybrid" UX:
 *   - Cheapest carrier shows by default as a single row.
 *   - Chevron expands to reveal every carrier the merchant allowed.
 *   - Selecting any carrier locks in that rate for order creation.
 *
 * On selection, we dispatch the FULL rate object to the reducer so
 * PaymentStep + checkoutPrismaNew can persist it verbatim onto
 * Order.metadata.terminalAfrica — Phase 6 dispatch reads it back to
 * book the actual shipment.
 */
const DeliveryOptionStep = ({
  state,
  dispatch,
  storeData,
  storeSlug,
  formatPrice,
  goToStep,
  goBack,
  cartItems = [],
}) => {
  // ── Chowdeck (existing) ──
  const [chowdeckOption, setChowdeckOption] = useState(null);
  const [loadingChowdeck, setLoadingChowdeck] = useState(true);
  const [chowdeckUnavailableReason, setChowdeckUnavailableReason] = useState(null);

  // ── Terminal Africa (Phase 5) ──
  const [terminalRates, setTerminalRates] = useState(null);
  const [loadingTerminal, setLoadingTerminal] = useState(true);
  const [terminalUnavailableReason, setTerminalUnavailableReason] = useState(null);
  const [terminalExpanded, setTerminalExpanded] = useState(false);
  const shippingItemsJson = JSON.stringify(cartItems.map(it => ({
    productId: it.product?.id || it.product?._id || it.productId,
    quantity: it.qty ?? it.quantity ?? 1,
  })).filter(it => it.productId));

  useEffect(() => {
    let cancelled = false;

    const checkChowdeck = async () => {
      setLoadingChowdeck(true);
      setChowdeckOption(null);
      setChowdeckUnavailableReason(null);
      if (!state.deliveryState) { setLoadingChowdeck(false); return; }
      try {
        const resp = await apiFetch('/chowdeck/calculate-fee', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            storeSlug,
            deliveryAddress: state.deliveryAddress,
            deliveryState: state.deliveryState,
            deliveryCity: state.deliveryCity,
          }),
        });
        if (cancelled) return;
        if (resp.ok) {
          const data = await resp.json();
          if (data.available) {
            const fee = data.deliveryFee ?? data.fee;
            if (data.feeId && typeof fee === 'number') {
              setChowdeckOption({
                fee,
                feeId: data.feeId,
                quoteToken: data.quoteToken,
                estimatedTime: data.estimatedTime || '30-60 mins',
              });
            } else {
              setChowdeckUnavailableReason('Unable to calculate express delivery fee');
            }
          } else {
            setChowdeckUnavailableReason(data.message || 'Express delivery not available for this location');
          }
        } else {
          const errorData = await resp.json().catch(() => ({}));
          setChowdeckUnavailableReason(errorData.error || 'Express delivery temporarily unavailable');
        }
      } catch (e) {
        if (!cancelled) setChowdeckUnavailableReason('Could not check express delivery availability');
      } finally {
        if (!cancelled) setLoadingChowdeck(false);
      }
    };

    const checkTerminal = async () => {
      setLoadingTerminal(true);
      setTerminalRates(null);
      setTerminalUnavailableReason(null);
      if (!state.deliveryState || !cartItems || cartItems.length === 0) {
        setLoadingTerminal(false);
        return;
      }
      try {
        const resp = await apiFetch('/terminal-africa/quote', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            storeSlug,
            deliveryAddress: {
              name: state.fullName || 'Customer',
              phone: state.whatsappNumber || state.phone || '',
              email: state.email || undefined,
              line1: state.deliveryAddress || '',
              city: state.deliveryCity || '',
              state: state.deliveryState || '',
              country: 'NG',
              postalCode: state.deliveryPostalCode || state.deliveryZipCode || '',
            },
            cartItems: JSON.parse(shippingItemsJson),
            currency: 'NGN',
          }),
        });
        if (cancelled) return;
        const data = await resp.json();
        if (data.available && Array.isArray(data.carriers) && data.carriers.length > 0) {
          setTerminalRates(data);
        } else {
          setTerminalUnavailableReason(data.message || 'Nationwide couriers not available for this address');
        }
      } catch (e) {
        if (!cancelled) setTerminalUnavailableReason('Could not check nationwide shipping availability');
      } finally {
        if (!cancelled) setLoadingTerminal(false);
      }
    };

    // Fire both in parallel — neither depends on the other.
    checkChowdeck();
    checkTerminal();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeSlug, state.deliveryAddress, state.deliveryState, state.deliveryCity, shippingItemsJson]);

  // ── Selection handlers ──
  const handleSelectStandard = () => {
    dispatch({
      type: 'SET_FIELDS',
      fields: {
        deliveryMethod: 'standard',
        chowdeckFeeId: null,
        chowdeckDeliveryFee: null,
        chowdeckQuoteToken: null,
        terminalAfrica: null,
        terminalAfricaFee: null,
      },
    });
  };

  const handleSelectChowdeck = () => {
    if (!chowdeckOption) return;
    dispatch({
      type: 'SET_FIELDS',
      fields: {
        deliveryMethod: 'CHOWDECK',
        chowdeckFeeId: chowdeckOption.feeId,
        chowdeckDeliveryFee: chowdeckOption.fee,
        chowdeckQuoteToken: chowdeckOption.quoteToken,
        terminalAfrica: null,
        terminalAfricaFee: null,
      },
    });
  };

  const handleSelectTerminal = (rate) => {
    if (!rate) return;
    dispatch({
      type: 'SET_FIELDS',
      fields: {
        deliveryMethod: 'TERMINAL_AFRICA',
        chowdeckFeeId: null,
        chowdeckDeliveryFee: null,
        chowdeckQuoteToken: null,
        // Full metadata for order create — checkoutPrismaNew persists
        // this verbatim so Phase 6 dispatch can book via the same rate.
        terminalAfrica: {
          rateId: rate.providerRateId,
          carrierName: rate.carrierName,
          carrierCode: rate.carrierCode,
          feeQuoted: rate.amount,
          currency: rate.currency,
          markupPercent: terminalRates?.markupPercent ?? null,
          rate,
        },
        terminalAfricaFee: rate.amount,
      },
    });
  };

  const handleContinue = () => { goToStep(STEPS.PAYMENT); };

  const standardShippingCost = state.shippingCalculation?.finalShippingCost != null
    ? Number(state.shippingCalculation.finalShippingCost)
    : (state.shippingCalculation?.shippingCost || 0);
  const estimatedDays = state.shippingCalculation?.estimatedDeliveryDays || 3;

  // Currently-selected Terminal rate id — for the collapsed row's
  // "picked" state when it matches the cheapest.
  const selectedTerminalRateId = state.deliveryMethod === 'TERMINAL_AFRICA'
    ? state.terminalAfrica?.rateId
    : null;

  return (
    <div className="cm-step-content">
      <div className="cm-delivery-options-list">
        {/* ── Standard shipping (from merchant zones) ── */}
        <div
          className={`cm-delivery-option${state.deliveryMethod === 'standard' ? ' selected' : ''}`}
          onClick={handleSelectStandard}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleSelectStandard(); }}
        >
          <div className="cm-delivery-option-radio">
            <div className={`cm-radio${state.deliveryMethod === 'standard' ? ' cm-radio-checked' : ''}`} />
          </div>
          <div className="cm-delivery-option-details">
            <div className="cm-delivery-option-label">Standard Delivery</div>
            <div className="cm-delivery-option-estimate">{estimatedDays} business days</div>
          </div>
          <div className="cm-delivery-option-price">
            {standardShippingCost === 0 ? 'FREE' : formatPrice(standardShippingCost)}
          </div>
        </div>

        {/* ── Chowdeck (hyperlocal fast delivery) ── */}
        {chowdeckOption && (
          <div
            className={`cm-delivery-option${state.deliveryMethod === 'CHOWDECK' ? ' selected' : ''}`}
            onClick={handleSelectChowdeck}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleSelectChowdeck(); }}
          >
            <div className="cm-delivery-option-radio">
              <div className={`cm-radio${state.deliveryMethod === 'CHOWDECK' ? ' cm-radio-checked' : ''}`} />
            </div>
            <div className="cm-delivery-option-details">
              <div className="cm-delivery-option-label">Express Delivery (Chowdeck)</div>
              <div className="cm-delivery-option-estimate">{chowdeckOption.estimatedTime}</div>
            </div>
            <div className="cm-delivery-option-price">{formatPrice(chowdeckOption.fee)}</div>
          </div>
        )}

        {/* ── Terminal Africa: A2 hybrid — cheapest by default, expand for all ── */}
        {terminalRates && terminalRates.carriers.length > 0 && (
          <div className="cm-ta-block">
            {/* Cheapest as the primary row */}
            <div
              className={`cm-delivery-option${
                selectedTerminalRateId === terminalRates.cheapest.providerRateId ? ' selected' : ''
              }`}
              onClick={() => handleSelectTerminal(terminalRates.cheapest)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') handleSelectTerminal(terminalRates.cheapest);
              }}
            >
              <div className="cm-delivery-option-radio">
                <div className={`cm-radio${
                  selectedTerminalRateId === terminalRates.cheapest.providerRateId ? ' cm-radio-checked' : ''
                }`} />
              </div>
              <div className="cm-delivery-option-details">
                <div className="cm-delivery-option-label">
                  {terminalRates.cheapest.carrierLogo && (
                    <img
                      src={terminalRates.cheapest.carrierLogo}
                      alt=""
                      className="cm-ta-carrier-logo"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  )}
                  Nationwide — {terminalRates.cheapest.carrierName}
                </div>
                <div className="cm-delivery-option-estimate">
                  {terminalRates.cheapest.estimatedDeliveryDays
                    ? `${terminalRates.cheapest.estimatedDeliveryDays} business day${terminalRates.cheapest.estimatedDeliveryDays === 1 ? '' : 's'}`
                    : 'Delivery time varies'}
                  {terminalRates.carriers.length > 1 && (
                    <span className="cm-ta-compare">
                      {' · '}
                      <button
                        type="button"
                        className="cm-ta-compare-btn"
                        onClick={(e) => { e.stopPropagation(); setTerminalExpanded((v) => !v); }}
                      >
                        {terminalExpanded ? 'Hide options' : `Compare ${terminalRates.carriers.length} carriers`}
                        <svg
                          width="12" height="12" viewBox="0 0 24 24"
                          fill="none" stroke="currentColor" strokeWidth="2.5"
                          className={`cm-ta-chevron${terminalExpanded ? ' cm-ta-chevron-open' : ''}`}
                          aria-hidden="true"
                        >
                          <polyline points="6 9 12 15 18 9" />
                        </svg>
                      </button>
                    </span>
                  )}
                </div>
              </div>
              <div className="cm-delivery-option-price">
                {formatPrice(terminalRates.cheapest.amount)}
              </div>
            </div>

            {/* Expanded — every alternative except the cheapest */}
            {terminalExpanded && terminalRates.carriers.slice(1).map((r) => (
              <div
                key={r.providerRateId}
                className={`cm-delivery-option cm-ta-alternate${
                  selectedTerminalRateId === r.providerRateId ? ' selected' : ''
                }`}
                onClick={() => handleSelectTerminal(r)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') handleSelectTerminal(r);
                }}
              >
                <div className="cm-delivery-option-radio">
                  <div className={`cm-radio${selectedTerminalRateId === r.providerRateId ? ' cm-radio-checked' : ''}`} />
                </div>
                <div className="cm-delivery-option-details">
                  <div className="cm-delivery-option-label">
                    {r.carrierLogo && (
                      <img
                        src={r.carrierLogo}
                        alt=""
                        className="cm-ta-carrier-logo"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    )}
                    {r.carrierName}
                  </div>
                  <div className="cm-delivery-option-estimate">
                    {r.estimatedDeliveryDays
                      ? `${r.estimatedDeliveryDays} business day${r.estimatedDeliveryDays === 1 ? '' : 's'}`
                      : 'Delivery time varies'}
                  </div>
                </div>
                <div className="cm-delivery-option-price">{formatPrice(r.amount)}</div>
              </div>
            ))}
          </div>
        )}

        {/* ── Loading + unavailable states ── */}
        {loadingChowdeck && (
          <div className="cm-delivery-loading">Checking express delivery availability...</div>
        )}
        {!loadingChowdeck && !chowdeckOption && chowdeckUnavailableReason && (
          <div className="cm-delivery-unavailable">
            <div className="cm-delivery-unavailable-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <div className="cm-delivery-unavailable-text">
              <span className="cm-delivery-unavailable-title">Express Delivery (Chowdeck)</span>
              <span className="cm-delivery-unavailable-reason">{chowdeckUnavailableReason}</span>
            </div>
          </div>
        )}
        {loadingTerminal && (
          <div className="cm-delivery-loading">Checking nationwide shipping availability...</div>
        )}
        {!loadingTerminal && !terminalRates && terminalUnavailableReason && (
          <div className="cm-delivery-unavailable">
            <div className="cm-delivery-unavailable-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <div className="cm-delivery-unavailable-text">
              <span className="cm-delivery-unavailable-title">Nationwide Shipping</span>
              <span className="cm-delivery-unavailable-reason">{terminalUnavailableReason}</span>
            </div>
          </div>
        )}
      </div>

      {state.error && <div className="cm-error">{state.error}</div>}

      <div className="cm-step-footer">
        <button className="cm-btn-secondary" onClick={goBack} type="button">Back</button>
        <button className="cm-btn-primary" onClick={handleContinue} type="button">
          Continue to Payment
        </button>
      </div>
    </div>
  );
};

export default DeliveryOptionStep;
